import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as adminService from '../../services/admin';

const BranchAccess = () => {
    const [users, setUsers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [branchAccess, setBranchAccess] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadUsers();
        loadBranches();
    }, []);

    const loadUsers = async () => {
        try {
            const response = await adminService.listUsers();
            if (response.status === 'success') {
                setUsers(response.data);
            }
        } catch (error) {
            console.error('Error loading users:', error);
            toast.error('Failed to load users');
        }
    };

    const loadBranches = async () => {
        try {
            const response = await adminService.listBranches();
            if (response.status === 'success') {
                setBranches(response.data);
            }
        } catch (error) {
            console.error('Error loading branches:', error);
            toast.error('Failed to load branches');
        }
    };

    const loadUserBranchAccess = async (userId) => {
        try {
            setLoading(true);
            const response = await adminService.listUserBranchAccess(userId);
            if (response.status === 'success') {
                setSelectedUser(response.data.user);
                setBranchAccess(response.data.branch_access);
            }
        } catch (error) {
            console.error('Error loading branch access:', error);
            toast.error('Failed to load branch access');
        } finally {
            setLoading(false);
        }
    };

    const handleUserChange = (e) => {
        const userId = e.target.value;
        if (userId) {
            loadUserBranchAccess(userId);
        } else {
            setSelectedUser(null);
            setBranchAccess([]);
        }
    };

    const handleAccessChange = (branchId, field, value) => {
        const existingAccess = branchAccess.find(a => a.branch_id === branchId);

        if (existingAccess) {
            // Update existing
            setBranchAccess(branchAccess.map(a =>
                a.branch_id === branchId ? { ...a, [field]: value } : a
            ));
        } else {
            // Add new
            const branch = branches.find(b => b.id === branchId);
            setBranchAccess([...branchAccess, {
                branch_id: branchId,
                branch_name: branch.branch_name,
                can_view: field === 'can_view' ? value : false,
                can_manage: field === 'can_manage' ? value : false
            }]);
        }
    };

    const handleSaveAccess = async () => {
        if (!selectedUser) {
            toast.error('Please select a user');
            return;
        }

        // Filter only branches with at least one permission
        const activeAccess = branchAccess.filter(a => a.can_view || a.can_manage);

        try {
            setLoading(true);
            const response = await adminService.manageBranchAccess(selectedUser.id, {
                branch_accesses: activeAccess.map(a => ({
                    branch_id: a.branch_id,
                    can_view: a.can_view,
                    can_manage: a.can_manage
                }))
            });

            if (response.status === 'success') {
                toast.success('Branch access updated successfully');
                loadUserBranchAccess(selectedUser.id);
            } else {
                toast.error(response.message || 'Failed to update branch access');
            }
        } catch (error) {
            console.error('Error saving branch access:', error);
            toast.error('Failed to save branch access');
        } finally {
            setLoading(false);
        }
    };

    const getBranchAccess = (branchId) => {
        return branchAccess.find(a => a.branch_id === branchId) || {
            can_view: false,
            can_manage: false
        };
    };

    return (
        <div className="container-fluid mt-3">
            <div className="card">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fa fa-building"></i> Branch Access Management
                    </h5>
                </div>
                <div className="card-body">
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Select User</label>
                            <select
                                className="form-select"
                                onChange={handleUserChange}
                                disabled={loading}
                            >
                                <option value="">-- Select User --</option>
                                {users.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.name} - {user.email}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {selectedUser && (
                            <div className="col-md-6 d-flex align-items-end">
                                <button
                                    className="btn btn-success"
                                    onClick={handleSaveAccess}
                                    disabled={loading}
                                >
                                    <i className="fa fa-save"></i> Save Branch Access
                                </button>
                            </div>
                        )}
                    </div>

                    {selectedUser && (
                        <>
                            <div className="alert alert-info">
                                <strong>User:</strong> {selectedUser.name} ({selectedUser.email})
                                <br />
                                <strong>Role:</strong> {selectedUser.role?.role_name || 'N/A'}
                                <br />
                                <small className="text-muted">
                                    Note: Super Admin has access to all branches by default
                                </small>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-bordered table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Branch Name</th>
                                            <th>Branch Code</th>
                                            <th className="text-center">Can View</th>
                                            <th className="text-center">Can Manage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {branches.map(branch => {
                                            const access = getBranchAccess(branch.id);
                                            return (
                                                <tr key={branch.id}>
                                                    <td>{branch.branch_name}</td>
                                                    <td>{branch.branch_code}</td>
                                                    <td className="text-center">
                                                        <div className="form-check d-inline-block">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={access.can_view}
                                                                onChange={(e) =>
                                                                    handleAccessChange(
                                                                        branch.id,
                                                                        'can_view',
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="form-check d-inline-block">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={access.can_manage}
                                                                onChange={(e) =>
                                                                    handleAccessChange(
                                                                        branch.id,
                                                                        'can_manage',
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="alert alert-warning mt-3">
                                <h6><i className="fa fa-info-circle"></i> Access Levels:</h6>
                                <ul className="mb-0">
                                    <li><strong>Can View:</strong> User can view data from this branch</li>
                                    <li><strong>Can Manage:</strong> User can create, edit, and delete records in this branch</li>
                                </ul>
                            </div>
                        </>
                    )}

                    {!selectedUser && (
                        <div className="text-center text-muted py-5">
                            <i className="fa fa-info-circle fa-3x mb-3"></i>
                            <p>Please select a user to manage branch access</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BranchAccess;
